import { Checkbox, rem } from '@mantine/core';
import { isCancelled, joinNames, mailboxSiblings, messageReply, tidyBlankLines } from '@reditelji/shared';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAiSummary } from '../api/ai';
import { useEvent } from '../api/hooks';
import { useSession } from '../api/session';
import {
  useThreadReading,
  useVolunteerMutations,
  useVolunteers,
  type MessageRow,
  type ThreadAnswer,
  type VolunteerRow,
} from '../api/volunteers';
import { ago } from '../lib/ui';
import { SendCard } from '../mail/SendCard';
import { mailShiftCount } from '../mail/sendState';
import { AiDraftCard } from './AiDraftCard';
import { recordedAt } from './cancelCopy';
import { daysSince, replyChipState, type ReplyChipState, type VolunteerRollup } from './derive';
import { CARD, INPUT, MONO, SECTION_LABEL, seg } from './ui';
import { type CoordinatorLabelOf, useCoordinatorLabel } from '../lib/people';

const DIR_META: Record<MessageRow['direction'], { label: string; bg: string; fg: string }> = {
  in: { label: 'PREJETO', bg: 'oklch(0.95 0.032 155)', fg: 'oklch(0.36 0.07 155)' },
  out: { label: 'POSLANO', bg: 'oklch(0.95 0.03 245)', fg: 'oklch(0.38 0.09 245)' },
  note: { label: 'ZABELEŽKA', bg: 'oklch(0.962 0.005 95)', fg: 'oklch(0.45 0.012 90)' },
};

/**
 * Only ever read on a sent row — an arriving one shows the sender's address
 * instead — so `imap` here means the one thing it can: a mail the coordinator
 * wrote from the Zoho predal rather than from here.
 */
const SRC_LABEL: Record<MessageRow['source'], string> = {
  imap: 'iz predala',
  app: 'iz aplikacije',
  manual: 'ročni vnos',
};

const BODY_TEXT: React.CSSProperties = {
  fontSize: rem(13.5),
  lineHeight: 1.5,
  marginTop: 4,
  color: 'oklch(0.38 0.012 90)',
  whiteSpace: 'pre-wrap',
};

/**
 * A message, with the mail it was written under folded away.
 *
 * Nine tenths of what a reply carries is the mail it answers, quoted back, and
 * a coordinator reading a thread is reading the answer. So the quote is behind
 * one click — behind it and not gone: the row still holds every character the
 * server sent, and `splitQuoted` refuses any seam it is not sure of, so a
 * shape it has not met renders exactly as it did before. Asked only of `imap`,
 * because our own rendered mail and a typed note carry no quote to find — a
 * mail the coordinator wrote in the predal is `imap` too, and comes back out
 * of the Sent folder wearing the volunteer's mail the same way.
 */
function MessageBody({ m }: { m: MessageRow }) {
  const [open, setOpen] = useState(false);
  // Memoised because CommsTab owns the compose box's state: without it every
  // keystroke there re-splits every message in the thread, and the thread is
  // fetched without a limit.
  // Split and tidied together, and memoised: the row keeps what the server
  // sent — a client's doubled paragraph spacing included, which pre-wrap would
  // show every line of — and CommsTab owns the compose box's state, so without
  // this every keystroke there would redo both for every message in the thread.
  const shown = useMemo(() => {
    const split = messageReply(m);
    return { reply: tidyBlankLines(split.reply), quoted: tidyBlankLines(split.quoted).trim() };
  }, [m.source, m.body, m.direction]);
  const quoted = shown.quoted;
  if (quoted === '') return <div style={BODY_TEXT}>{shown.reply}</div>;
  return (
    <>
      <div style={BODY_TEXT}>{shown.reply}</div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={
          m.direction === 'out'
            ? 'Sporočilo, na katerega je bil to odgovor.'
            : 'Sporočilo, na katerega je prostovoljec odgovoril.'
        }
        style={{
          marginTop: 8,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          padding: '2px 9px 2px 7px',
          borderRadius: 20,
          fontFamily: 'var(--rr-font)',
          fontSize: rem(11.5),
          fontWeight: 600,
          background: 'oklch(0.985 0.02 75)',
          border: '1px solid oklch(0.9 0.05 75)',
          color: 'oklch(0.42 0.09 75)',
        }}
      >
        <span style={{ fontFamily: MONO, fontSize: rem(9.5) }}>{open ? '▼' : '▶'}</span>
        citirano · {quoted.length.toLocaleString('sl-SI')} znakov
      </button>
      {open ? (
        <div
          style={{
            marginTop: 8,
            padding: '8px 11px',
            borderLeft: '2px solid oklch(0.9 0.05 75)',
            borderRadius: '0 7px 7px 0',
            background: 'oklch(0.965 0.005 95)',
            fontSize: rem(12.5),
            lineHeight: 1.45,
            color: 'oklch(0.55 0.012 90)',
            whiteSpace: 'pre-wrap',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {quoted}
        </div>
      ) : null}
    </>
  );
}

type ThreadItem =
  | { kind: 'message'; key: string; at: string; message: MessageRow }
  | { kind: 'answer'; key: string; at: string; answer: ThreadAnswer };

/**
 * The messages with the answers given on the page between them, by time — an
 * answer recorded in the same instant as a message goes after it. The summary
 * on Profil counts messages only; this list is for reading.
 */
function threadItems(messages: readonly MessageRow[], answers: readonly ThreadAnswer[]): ThreadItem[] {
  const items: ThreadItem[] = [
    ...messages.map((m) => ({ kind: 'message' as const, key: m.id, at: m.at, message: m })),
    ...answers.map((a, i) => ({ kind: 'answer' as const, key: `answer-${i}-${a.at}`, at: a.at, answer: a })),
  ];
  const t = (x: ThreadItem) => new Date(x.at).getTime();
  return items.sort((a, b) => t(a) - t(b) || (a.kind === b.kind ? 0 : a.kind === 'message' ? -1 : 1));
}

const REPLY_LABEL = {
  out: { asks: 'pričakujemo odgovor', closes: 'ne potrebuje odgovora' },
  in: { asks: 'čaka naš odgovor', closes: 'ni treba odgovoriti' },
} as const;

const CHIP_TONE = {
  waits: { bg: 'oklch(0.985 0.015 15)', bd: 'oklch(0.9 0.04 15)', fg: 'oklch(0.45 0.12 25)' },
  asks: { bg: 'oklch(0.985 0.02 75)', bd: 'oklch(0.9 0.05 75)', fg: 'oklch(0.42 0.09 75)' },
  closes: { bg: 'oklch(0.975 0.02 155)', bd: 'oklch(0.88 0.04 155)', fg: 'oklch(0.42 0.08 155)' },
  auto: { bg: 'oklch(0.965 0.005 95)', bd: 'oklch(0.9 0.008 95)', fg: 'oklch(0.5 0.012 90)' },
};

/**
 * Whether the conversation's deciding message waits for an answer, and who
 * said so — the rules, the model or a coordinator. A click turns it round;
 * "ponastavi" takes a coordinator's word back.
 */
function ReplyChip({
  m,
  state,
  canEdit,
  labelOf,
}: {
  m: MessageRow;
  state: ReplyChipState;
  canEdit: boolean;
  labelOf: CoordinatorLabelOf;
}) {
  const mut = useVolunteerMutations();
  const chip = (tone: keyof typeof CHIP_TONE, clickable: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: rem(11),
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 20,
    border: `1px solid ${CHIP_TONE[tone].bd}`,
    background: CHIP_TONE[tone].bg,
    color: CHIP_TONE[tone].fg,
    cursor: clickable ? 'pointer' : 'default',
  });
  const source = (text: string) => (
    <span style={{ fontFamily: MONO, fontSize: rem(10), fontWeight: 600, opacity: 0.75 }}>{text}</span>
  );
  const reset =
    canEdit && m.replyOverride ? (
      <button
        type="button"
        style={{ border: 0, background: 'none', padding: 0, fontSize: rem(11), color: 'oklch(0.55 0.012 90)', cursor: 'pointer' }}
        onClick={() => mut.setReplyClass.mutate({ messageId: m.id, cls: null })}
      >
        ponastavi
      </button>
    ) : null;
  if (state.kind === 'settled') {
    return (
      <span style={chip('closes', false)} title="Na to sporočilo nihče več ne čaka.">
        {state.text}
      </span>
    );
  }
  if (state.kind === 'auto') {
    // Turned round, it is a person's word after all — somebody wrote "Odsotna"
    // themselves, or a responder said more than it should.
    return (
      <button
        type="button"
        disabled={!canEdit || mut.setReplyClass.isPending}
        style={chip('auto', canEdit)}
        title={canEdit ? 'Samodejni odgovor ali vrnjena pošta: ne šteje kot odgovor. Klikni, če ga je napisal človek.' : undefined}
        onClick={() => mut.setReplyClass.mutate({ messageId: m.id, cls: 'asks' })}
      >
        samodejni odgovor · ne šteje
      </button>
    );
  }
  const { cls } = state;
  const tone = cls === 'closes' ? 'closes' : m.direction === 'in' ? 'waits' : 'asks';
  const by = state.by === 'coordinator' ? labelOf(m.replyOverrideBy) : state.by === 'ai' ? 'AI' : 'pravila';
  const direction = m.direction === 'in' ? 'in' : 'out';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        disabled={!canEdit || mut.setReplyClass.isPending}
        title={canEdit ? 'Klikni, če oznaka ne drži.' : undefined}
        style={chip(tone, canEdit)}
        onClick={() => mut.setReplyClass.mutate({ messageId: m.id, cls: cls === 'asks' ? 'closes' : 'asks' })}
      >
        {REPLY_LABEL[direction][cls]} {source(by)}
      </button>
      {reset}
    </span>
  );
}

function AnswerRow({ answer: a, labelOf }: { answer: ThreadAnswer; labelOf: CoordinatorLabelOf }) {
  const text =
    a.actorType === 'volunteer'
      ? a.status === 'potrjen'
        ? 'Potrdil razpored na strani'
        : 'Zavrnil razpored na strani'
      : // A refusal says why; a confirmation's comment has only ever been the app's own words.
        `${a.status === 'potrjen' ? 'Potrditev' : 'Zavrnitev'} zabeležil ${labelOf(a.actorId)}${
          a.status === 'zavrnjen' && a.comment ? ` · ${a.comment}` : ''
        }`;
  return (
    <div
      style={{
        margin: '0 48px',
        padding: '6px 12px',
        border: '1px dashed oklch(0.9 0.008 95)',
        borderRadius: 9,
        background: 'oklch(0.975 0.004 95)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        fontSize: rem(12),
        color: 'oklch(0.48 0.012 90)',
      }}
    >
      <span
        style={{
          fontFamily: MONO,
          fontSize: rem(10.5),
          fontWeight: 700,
          letterSpacing: '0.04em',
          padding: '1px 6px',
          borderRadius: 4,
          background: a.status === 'potrjen' ? 'oklch(0.95 0.032 155)' : 'oklch(0.96 0.03 25)',
          color: a.status === 'potrjen' ? 'oklch(0.36 0.07 155)' : 'oklch(0.45 0.1 25)',
        }}
      >
        {a.actorType === 'volunteer' ? 'STRAN' : 'VNOS'}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{text}</span>
      <span style={{ fontFamily: MONO, fontSize: rem(11), color: 'oklch(0.6 0.012 90)' }}>
        {recordedAt(a.at)}
      </span>
    </div>
  );
}

export function CommsTab({
  volunteer: v,
  rollup: r,
  highlightId,
}: {
  volunteer: VolunteerRow;
  rollup: VolunteerRollup;
  /** Message cited by a bullet of the summary on Profil — scrolled to and highlighted. */
  highlightId: string | null;
}) {
  const thread = useThreadReading(v.id);
  const labelOf = useCoordinatorLabel();
  const mut = useVolunteerMutations();
  const event = useEvent();
  const roster = useVolunteers();
  // The others who read this person's inbox: the thread says so, a mirrored
  // row says who else holds it, and a logged call can be filed with them.
  const siblings = mailboxSiblings(v, roster.data ?? []);
  const aiEnabled = event.data?.ai.enabled ?? false;
  // Reading the thread is what brings the summary on Profil up to date: the API
  // regenerates it here when the thread changed, and nowhere a profile just opens.
  useAiSummary(v.id, aiEnabled);
  const [body, setBody] = useState('');
  const [direction, setDirection] = useState<'note' | 'in' | 'out'>('note');
  const [alsoTo, setAlsoTo] = useState<string[]>([]);
  // The cited message is scrolled into view. The thread is already cached when
  // a citation is clickable, so its rows are mounted by the time this runs.
  const msgRefs = useRef(new Map<string, HTMLDivElement>());
  useEffect(() => {
    if (!highlightId) return;
    msgRefs.current.get(highlightId)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightId]);

  const msgs = useMemo(() => thread.data?.messages ?? [], [thread.data]);
  const deciding = thread.data?.deciding;
  const canEditReply = useSession().can('comms:write');
  const items = useMemo(() => threadItems(msgs, thread.data?.answers ?? []), [msgs, thread.data]);
  // Somebody who is not coming is not chased (SPEC §1.1K); what they wrote to
  // us still waits for an answer.
  const out = isCancelled(v);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 16, alignItems: 'start' }}>
      {/* thread */}
      <div style={{ ...CARD, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '13px 16px 12px',
            borderBottom: '1px solid oklch(0.94 0.005 95)',
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'oklch(0.62 0.09 155)', flexShrink: 0 }} />
          <span style={{ fontFamily: MONO, fontSize: rem(12.5), fontWeight: 600 }}>{v.email ?? 'brez e-naslova'}</span>
          {siblings.length > 0 ? (
            <span
              title="Isti e-naslov berejo tudi drugi: pošta v ta predal je v vseh njihovih nitih."
              style={{
                fontSize: rem(11.5),
                padding: '2px 8px',
                borderRadius: 20,
                background: 'oklch(0.955 0.03 75)',
                color: 'oklch(0.42 0.08 75)',
              }}
            >
              skupni predal z {joinNames(siblings.map((s) => s.name), 'sl')}
            </span>
          ) : null}
          <span style={{ fontSize: rem(12), color: 'oklch(0.6 0.012 90)' }}>
            {msgs.length
              ? `${msgs.length} sporočil · zadnje ${ago(daysSince(msgs.at(-1)!.at))}`
              : 'pogovor s prostovoljcem'}
          </span>
          <div style={{ flex: 1 }} />
          <span
            style={{
              fontSize: rem(11.5),
              padding: '2px 8px',
              borderRadius: 20,
              background: 'oklch(0.965 0.005 95)',
              color: 'oklch(0.5 0.012 90)',
            }}
          >
            e-pošta + ročni vnosi
          </span>
        </div>
        <div style={{ padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {thread.isPending ? (
            <div style={{ fontSize: rem(13), color: 'oklch(0.6 0.012 90)', padding: '6px 0' }}>Nalagam pogovor…</div>
          ) : null}
          {items.map((item) => {
            if (item.kind === 'answer') return <AnswerRow key={item.key} answer={item.answer} labelOf={labelOf} />;
            const m = item.message;
            const dir = DIR_META[m.direction];
            const chipState = replyChipState(m, deciding);
            const hl = m.id === highlightId;
            return (
              <div
                key={m.id}
                ref={(el) => {
                  if (el) msgRefs.current.set(m.id, el);
                  else msgRefs.current.delete(m.id);
                }}
                style={{
                  border: `1px solid ${hl ? 'oklch(0.75 0.08 245)' : m.direction === 'in' ? 'oklch(0.9 0.03 155)' : 'oklch(0.93 0.006 95)'}`,
                  borderRadius: 9,
                  padding: '11px 13px',
                  background: hl ? 'oklch(0.98 0.025 245)' : m.direction === 'in' ? 'oklch(0.99 0.008 155)' : 'oklch(1 0 0)',
                  transition: 'background 0.3s, border-color 0.3s',
                  marginLeft: m.direction === 'in' ? 0 : 26,
                  marginRight: m.direction === 'in' ? 26 : 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontFamily: MONO,
                      fontSize: rem(11),
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      padding: '1px 6px',
                      borderRadius: 4,
                      background: dir.bg,
                      color: dir.fg,
                    }}
                  >
                    {dir.label}
                  </span>
                  <span style={{ fontSize: rem(12.5), fontWeight: 600, color: 'oklch(0.3 0.012 90)' }}>
                    {m.direction === 'in'
                      ? m.fromAddress || v.name
                      : m.createdById
                        ? `${SRC_LABEL[m.source]} · ${labelOf(m.createdById)}`
                        : SRC_LABEL[m.source]}
                  </span>
                  {m.source === 'app' ? (
                    <span
                      style={{
                        fontSize: rem(11),
                        fontWeight: 700,
                        letterSpacing: '0.03em',
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: 'oklch(0.95 0.03 245)',
                        color: 'oklch(0.4 0.08 245)',
                      }}
                    >
                      POSLANO IZ APLIKACIJE
                    </span>
                  ) : null}
                  {m.alsoTo && m.alsoTo.length > 0 ? (
                    <span
                      title="Isto sporočilo je tudi v niti oseb, ki delijo ta predal."
                      style={{
                        fontSize: rem(11),
                        fontWeight: 600,
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: 'oklch(0.955 0.03 75)',
                        color: 'oklch(0.42 0.08 75)',
                      }}
                    >
                      tudi: {m.alsoTo.map((a) => a.name).join(', ')}
                    </span>
                  ) : null}
                  <div style={{ flex: 1 }} />
                  <span style={{ fontFamily: MONO, fontSize: rem(11), color: 'oklch(0.6 0.012 90)' }}>
                    {new Date(m.at).toLocaleString('sl-SI', {
                      day: 'numeric',
                      month: 'numeric',
                      hour: '2-digit',
                      hourCycle: 'h23',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                {m.subject ? (
                  <div style={{ fontSize: rem(13.5), fontWeight: 600, marginTop: 7, color: 'oklch(0.26 0.012 90)' }}>
                    {m.subject}
                  </div>
                ) : null}
                <MessageBody m={m} />
                {chipState ? (
                  <div style={{ marginTop: 8 }}>
                    <ReplyChip m={m} state={chipState} canEdit={canEditReply} labelOf={labelOf} />
                  </div>
                ) : null}
              </div>
            );
          })}
          {!thread.isPending && msgs.length === 0 ? (
            <div style={{ fontSize: rem(13), color: 'oklch(0.6 0.012 90)', padding: '6px 0' }}>
              Še ni sporočil. Zabeleži klic ali pošlji navodila.
            </div>
          ) : null}

          {/* manual log */}
          <div
            style={{
              borderTop: '1px dashed oklch(0.92 0.006 95)',
              paddingTop: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 7,
            }}
          >
            <div style={{ display: 'flex', gap: 5 }}>
              {(
                [
                  ['note', 'Zabeležka'],
                  ['in', 'Prejet klic/mail'],
                  ['out', 'Oddan klic/mail'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setDirection(value)}
                  style={{
                    cursor: 'pointer',
                    padding: '5px 10px',
                    borderRadius: 6,
                    fontSize: rem(12.5),
                    fontFamily: 'inherit',
                    ...seg(direction === value),
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.currentTarget.value)}
              placeholder="Kaj sta se dogovorila?"
              style={{
                ...INPUT,
                width: '100%',
                boxSizing: 'border-box',
                minHeight: 54,
                resize: 'vertical',
                lineHeight: 1.45,
                background: 'oklch(0.99 0.003 95)',
              }}
            />
            {siblings.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: rem(12), color: 'oklch(0.5 0.012 90)' }}>Zabeleži tudi pri:</span>
                {siblings.map((s) => (
                  <Checkbox
                    key={s.id}
                    size="xs"
                    label={s.name}
                    checked={alsoTo.includes(s.id)}
                    onChange={(e) =>
                      setAlsoTo(
                        e.currentTarget.checked
                          ? [...alsoTo, s.id]
                          : alsoTo.filter((id) => id !== s.id),
                      )
                    }
                  />
                ))}
              </div>
            ) : null}
            <button
              disabled={!body.trim() || mut.logMessage.isPending}
              onClick={() => {
                mut.logMessage.mutate({ volunteerId: v.id, direction, body: body.trim(), alsoTo });
                setBody('');
                setAlsoTo([]);
              }}
              style={{
                alignSelf: 'flex-start',
                cursor: body.trim() ? 'pointer' : 'default',
                padding: '7px 14px',
                borderRadius: 7,
                border: 0,
                background: body.trim() ? 'oklch(0.35 0.05 245)' : 'oklch(0.93 0.006 95)',
                color: body.trim() ? 'oklch(1 0 0)' : 'oklch(0.6 0.012 90)',
                fontSize: rem(13),
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              Zabeleži
            </button>
          </div>
        </div>
      </div>

      {/* right column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
        <AiDraftCard
          volunteerId={v.id}
          volunteerEmail={v.email}
          enabled={aiEnabled}
          // A reminder for somebody who owes an answer and has not written; a reply otherwise.
          defaultKind={!out && !r.awaitingUs && (r.noReply || v.confirmationStatus === 'čaka') ? 'chase' : 'reply'}
        />

        <SendCard
          volunteer={v}
          shiftCount={mailShiftCount(r.shifts)}
          cardStyle={CARD}
          labelStyle={SECTION_LABEL}
        />
      </div>
    </div>
  );
}
